import { useState } from "react";
import { C, cardRules, mechanics } from "@bd/content";
import { defaultDesign, LINE_LABEL, LINE_WORK, lineDesign, lineOptions, lineRoot, money, type Card, type LineKind } from "@bd/sim";
import { Sheet, Why } from "@bd/ui-kit";
import { t, tp } from "../i18n";
import { me, useGame } from "../store";
import { cover } from "../ui/Hud";
import { Fact } from "./ConceptPicker";

const BLURB: Record<LineKind, string> = {
  expansion: "More of the same game, for people who own it. Fans tire after three.",
  edition: "Better parts, higher price. Brings an old hit back.",
  spinoff: "Same theme, new mechanic. Fans of the original follow it.",
  card_set: "New cards for core-set players. Design up to three headline cards; the rest fill in.",
  rpg_supplement: "New adventures for core-book players. Good writing grows the fan base.",
};

/** Start a follow-up to one of your games: expansion, deluxe edition, spin-off, card set or RPG supplement. */
export function FollowUp({ productId, kind }: { productId: string; kind: LineKind }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game)!;
  const parent = co.products.find((p) => p.id === productId);
  const base = parent ? lineDesign(game, co, parent, kind) : null;
  const [name, setName] = useState(base?.name ?? "");
  const [mech, setMech] = useState<string>(() => mechanics.find((m) => m.id !== parent?.design.mechanic && co.unlocked.includes(`m:${m.id}`))?.id ?? "");
  const top = parent ? lineRoot(co, parent).meta?.top : undefined;
  const [cards, setCards] = useState<Partial<Card>[]>(() => kind === "card_set" ? [0, 1, 2].map((i) => ({ name: `${cardRules.cardWords.first[(i * 5 + 3) % 16]} ${cardRules.cardWords.second[(i * 7 + 2) % 16]}`, keyword: cardRules.keywords[(i * 4) % 12].id, power: 6 })) : []);
  const [err, setErr] = useState<string | null>(null);
  if (!parent || !base) return <Sheet title={t("Gone")} onClose={() => set({ screen: null })}><p>{t("That game is gone.")}</p></Sheet>;
  const opt = lineOptions(game, co, parent).find((o) => o.kind === kind);
  const design = kind === "spinoff" && mech ? defaultDesign(name, mech, parent.design.theme, parent.design.audience, C.mechanic[mech].formats.find((f) => co.unlocked.includes(`f:${f}`)) ?? "card", game.week) : { ...base, name };

  const start = async () => {
    setErr(null);
    const r = await cmd({ type: "startProject", design, kind, parentId: parent.id, cards: kind === "card_set" ? cards : undefined });
    if (!r.ok) return setErr(r.error);
    toast({ title: t("Work begins on {name}", { name: design.name }), tone: "good" });
    set({ screen: null });
    useGame.getState().resume();
  };
  const updCard = (i: number, p: Partial<Card>) => setCards((cs) => cs.map((c, j) => (j === i ? { ...c, ...p } : c)));

  return (
    <Sheet title={`${t(LINE_LABEL[kind])}: ${parent.design.name}`} onClose={() => set({ screen: { kind: "products" } })} wide testId="followup"
      footer={<>
        {err ? <span className="bad dd-foot-note" role="alert">{t(err)}</span> : (
          <span className="facts muted dd-foot-note" style={{ fontSize: "0.9rem" }}>
            <Fact icon="staff" title={LINE_WORK[kind] >= 1 ? t("As much work as a new game") : t("About {pct}% of a new game's work", { pct: Math.round(LINE_WORK[kind] * 100) })}>{Math.round(LINE_WORK[kind] * 100)}%</Fact>
            <Fact icon="coin" title={t("price {price}", { price: money(design.price) })}>{money(design.price)}</Fact>
          </span>
        )}
        <button className="btn primary" onClick={start} disabled={!opt?.ok || !name.trim()} data-testid="start-followup">{t("Start work")}</button>
      </>}>
      <div className="row" style={{ gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <img src={cover(design.name || parent.design.name, design.theme, design.mechanic, co.color, 0.7, 2, false, design.audience)} width={192} height={144} alt="" style={{ imageRendering: "pixelated", borderRadius: 8 }} />
        <div className="stack" style={{ flex: 1, minWidth: 260 }}>
          {opt && !opt.ok && <p className="bad" style={{ margin: 0 }} role="alert">{t(opt.why)}</p>}
          <label className="field">
            <input value={name} maxLength={48} placeholder={t("Name your game")} aria-label={t("Name")} onChange={(e) => setName(e.target.value)} data-testid="followup-name" />
          </label>
          <span className="facts muted" style={{ fontSize: "0.88rem" }} title={t("Base game: {name}", { name: parent.design.name })}>
            <Fact icon="star" title={t("score {score}", { score: parent.score.toFixed(1) })}>{parent.score.toFixed(1)}</Fact>
            <Fact icon="fans" title={tp(parent.sold, "{n} owner", "{n} owners")}>{parent.sold.toLocaleString("en-US")}</Fact>
            <Fact icon="heart" title={t("Would play again")}>{Math.round(lineRoot(co, parent).trueReplay * 100)}%</Fact>
            <Why title={t("Who buys it")} lines={[{ label: t("Owners"), value: lineRoot(co, parent).sold.toLocaleString("en-US") }, { label: t("Would play again"), value: `${Math.round(lineRoot(co, parent).trueReplay * 100)}%` }, { label: t("Earlier follow-ups"), value: co.products.filter((p) => p.parentId === parent.id).length }]} note={`${t(BLURB[kind])} ${t("Owners who replay it a lot buy more.")}`} />
          </span>
          {kind === "spinoff" && (
            <label className="field">
              <span className="caps">{t("Mechanic")}</span>
              <select value={mech} onChange={(e) => setMech(e.target.value)} aria-label={t("Spin-off mechanic")} data-testid="spinoff-mechanic">
                {mechanics.filter((m) => m.id !== parent.design.mechanic && co.unlocked.includes(`m:${m.id}`)).map((m) => <option key={m.id} value={m.id}>{t(m.name)}</option>)}
              </select>
            </label>
          )}
          {kind === "edition" && (
            <span className="facts muted" style={{ fontSize: "0.88rem" }}>{(() => {
              const up = Object.keys(design.parts).filter((k) => design.parts[k as keyof typeof design.parts] !== parent.design.parts[k as keyof typeof parent.design.parts]).length;
              const total = Object.keys(design.parts).length;
              return <Fact icon="layers" title={t("Upgraded parts: {n} of {total}", { n: up, total })}>{up}/{total}</Fact>;
            })()}</span>
          )}
        </div>
      </div>
      {kind === "card_set" && (
        <div className="stack" style={{ marginTop: 14 }}>
          <span className="caps">{t("Headline cards")}</span>
          {top && <span className="facts muted"><Fact icon="trophy" title={t("Current meta: {deck} decks win {pct}% of games. An answer card weakens a deck.", { deck: t(C.archetype[top].name), pct: Math.round((lineRoot(co, parent).meta?.dominance ?? 0) * 100) })}>{t(C.archetype[top].name)} · {Math.round((lineRoot(co, parent).meta?.dominance ?? 0) * 100)}%</Fact></span>}
          <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
            {cards.map((c, i) => (
              <div key={i} className="card stack" style={{ gap: 6 }} data-testid="headline-card">
                <input value={c.name ?? ""} maxLength={30} onChange={(e) => updCard(i, { name: e.target.value })} aria-label={t("Card {n} name", { n: i + 1 })} />
                <select value={c.keyword} onChange={(e) => updCard(i, { keyword: e.target.value })} aria-label={t("Card {n} keyword", { n: i + 1 })}>
                  {cardRules.keywords.map((k) => <option key={k.id} value={k.id}>{t(k.name)} · {t(C.archetype[k.archetype].name)}</option>)}
                </select>
                <label className="muted" style={{ fontSize: "0.82rem" }}>{t("Power {n}", { n: c.power ?? 0 })}{(c.power ?? 0) >= 9 ? ` · ${t("ban bait")}` : ""}
                  <input type="range" className="slider" min={1} max={10} value={c.power} onChange={(e) => updCard(i, { power: Number(e.target.value) })} aria-label={t("Card {n} power", { n: i + 1 })} />
                </label>
                <select value={c.answer ?? ""} onChange={(e) => updCard(i, { answer: e.target.value || undefined })} aria-label={t("Card {n} answers", { n: i + 1 })}>
                  <option value="">{t("Not an answer card")}</option>
                  {cardRules.archetypes.map((a) => <option key={a.id} value={a.id}>{t("Answer to {deck}", { deck: t(a.name) })}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
}
